/**
 * CustomerContext — "View as Customer" picker for Hub admins (2026-06-29).
 *
 * Pattern:
 *  - Hub admins (manager/admin roles): can switch view across any customer in
 *    the current industry. Their selection drives DynamicCardRenderer's
 *    organization_id parameter (which then resolves the customer-specific
 *    card overrides + overlay).
 *  - Spoke / org-scoped users: locked to their own organization_id. The
 *    picker is hidden.
 *  - "All customers" (null) shows industry-shared cards only — useful for
 *    Hub admins doing a "what's the platform baseline" check.
 *
 * Persistence: localStorage key `view-as-customer-id`. Clears on logout.
 * Listening: any component that uses `useCustomer()` re-renders on switch.
 *
 * Does NOT replace AuthContext.organizationId — that remains the true
 * identity. CustomerContext is a "view override" on top.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { useIndustry } from './IndustryContext'
import { get } from '../services/api'

export interface CustomerOption {
  id: string
  slug: string
  name: string
  industry?: string
}

interface CustomerContextValue {
  /** Effective org id used by data-fetching hooks. Null = "industry-wide / no override". */
  organizationId: string | null
  /** Display name of the currently-selected customer. */
  customerName: string | null
  /** Full list of customers available for the current industry (admin only). */
  customers: CustomerOption[]
  /** Whether the picker UI should be shown — false for org-scoped Spoke users. */
  canSwitchCustomer: boolean
  /** Set the active customer. Pass null to revert to "all / industry-wide". */
  setCustomer: (orgId: string | null) => void
  /** Refresh the customer list from the API. */
  refresh: () => Promise<void>
  loading: boolean
}

const CustomerContext = createContext<CustomerContextValue | null>(null)
const STORAGE_KEY = 'view-as-customer-id'

export function CustomerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { industryId } = useIndustry()

  // Hub admin = role in {admin, manager, superadmin}. Spoke users see only their own org.
  const role = ((user as any)?.role || (user as any)?.roles?.[0] || '').toString().toLowerCase()
  const canSwitchCustomer = ['admin', 'manager', 'superadmin', 'support', 'sales'].includes(role)

  const ownOrgId = ((user as any)?.organizationId || (user as any)?.organization_id || null) as string | null

  // selectedOrgId is what the user ACTIVELY chose. ownOrgId is the fallback.
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(() => {
    if (typeof window !== 'undefined' && canSwitchCustomer) {
      return window.localStorage.getItem(STORAGE_KEY) || null
    }
    return null
  })
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [loading, setLoading] = useState(false)

  // Effective org id passed to data hooks
  const organizationId = canSwitchCustomer ? selectedOrgId : ownOrgId

  const refresh = useCallback(async () => {
    if (!canSwitchCustomer) return
    setLoading(true)
    try {
      const qs = industryId ? `?industry_id=${encodeURIComponent(industryId)}` : ''
      const data = await get<{ organizations: CustomerOption[] }>(`/orgs/list${qs}`)
      setCustomers(data.organizations || [])
    } catch (e) {
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }, [canSwitchCustomer, industryId])

  useEffect(() => { refresh() }, [refresh])

  // If the currently-selected customer no longer belongs to the active
  // industry (after industry switch), clear it.
  useEffect(() => {
    if (!selectedOrgId || customers.length === 0) return
    const stillAvailable = customers.some(c => c.id === selectedOrgId)
    if (!stillAvailable) {
      setSelectedOrgId(null)
      if (typeof window !== 'undefined') window.localStorage.removeItem(STORAGE_KEY)
    }
  }, [industryId, customers, selectedOrgId])

  const setCustomer = useCallback((orgId: string | null) => {
    setSelectedOrgId(orgId)
    if (typeof window !== 'undefined') {
      if (orgId) window.localStorage.setItem(STORAGE_KEY, orgId)
      else window.localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  const customerName = useMemo(() => {
    const id = organizationId
    if (!id) return null
    const hit = customers.find(c => c.id === id)
    return hit?.name || null
  }, [organizationId, customers])

  const value: CustomerContextValue = {
    organizationId, customerName, customers, canSwitchCustomer,
    setCustomer, refresh, loading,
  }
  return <CustomerContext.Provider value={value}>{children}</CustomerContext.Provider>
}

export function useCustomer(): CustomerContextValue {
  const ctx = useContext(CustomerContext)
  if (!ctx) {
    // Safe fallback so a missing provider doesn't crash app
    return {
      organizationId: null, customerName: null, customers: [],
      canSwitchCustomer: false, setCustomer: () => {},
      refresh: async () => {}, loading: false,
    }
  }
  return ctx
}
